/**
 * @fileoverview Static flight time estimates from 6 hub cities to global destinations.
 *
 * Hub keys:
 *   NYC - New York City
 *   LAX - Los Angeles
 *   CHI - Chicago
 *   MIA - Miami
 *   LON - London
 *   TOR - Toronto
 *
 * Each destination maps to an object of { hubKey: flightHours }.
 * HOME_CITY_MAP resolves user home city aliases to the nearest hub key.
 * getStaticFlightEstimate performs a safe lookup with word-boundary matching
 * for short keys to avoid substring collisions (e.g. 'la' matching 'auckland').
 *
 * Extracted from web/src/WanderAppComplete.jsx (lines 4549-5020).
 */

import { reconcileTransportTimes } from './transportReconciler.js';

export const FLIGHT_TIMES = {
  // ==================== UNITED STATES ====================
  // Northeast
  'boston': { NYC: 1, LAX: 6, CHI: 2.5, MIA: 3.5, LON: 7, TOR: 1.5 },
  'new york': { NYC: 0, LAX: 6, CHI: 2.5, MIA: 3, LON: 7, TOR: 1.5 },
  'nyc': { NYC: 0, LAX: 6, CHI: 2.5, MIA: 3, LON: 7, TOR: 1.5 },
  'philadelphia': { NYC: 1, LAX: 5.5, CHI: 2, MIA: 3, LON: 7.5, TOR: 1.5 },
  'washington': { NYC: 1, LAX: 5.5, CHI: 2, MIA: 2.5, LON: 8, TOR: 1.5 },
  'dc': { NYC: 1, LAX: 5.5, CHI: 2, MIA: 2.5, LON: 8, TOR: 1.5 },
  'pittsburgh': { NYC: 1.5, LAX: 5, CHI: 1.5, MIA: 3, LON: 8, TOR: 1.5 },
  'pennsylvania': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 3, LON: 8, TOR: 1.5 },
  'nemacolin': { NYC: 1.5, LAX: 5, CHI: 1.5, MIA: 3, LON: 8, TOR: 1.5 },
  'farmington': { NYC: 1.5, LAX: 5, CHI: 1.5, MIA: 3, LON: 8, TOR: 1.5 },
  'poconos': { NYC: 1, LAX: 5.5, CHI: 2, MIA: 3, LON: 8, TOR: 1.5 },
  'hershey': { NYC: 1.5, LAX: 5.5, CHI: 2, MIA: 3, LON: 8, TOR: 1.5 },
  'bedford springs': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 3, LON: 8, TOR: 1.5 },
  'greenbrier': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 2.5, LON: 8.5, TOR: 2 },
  'west virginia': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 2.5, LON: 8.5, TOR: 2 },
  'homestead': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 2.5, LON: 8, TOR: 2 },

  // Southeast
  'miami': { NYC: 3, LAX: 5.5, CHI: 3.5, MIA: 0, LON: 9, TOR: 3.5 },
  'florida': { NYC: 3, LAX: 5.5, CHI: 3, MIA: 0, LON: 9, TOR: 3 },
  'orlando': { NYC: 2.5, LAX: 5, CHI: 3, MIA: 1, LON: 9, TOR: 3 },
  'tampa': { NYC: 3, LAX: 5.5, CHI: 3, MIA: 1, LON: 9, TOR: 3 },
  'keys': { NYC: 3.5, LAX: 6, CHI: 4, MIA: 1, LON: 10, TOR: 4 },
  'palm beach': { NYC: 3, LAX: 5.5, CHI: 3.5, MIA: 0.5, LON: 9, TOR: 3.5 },
  'fort lauderdale': { NYC: 3, LAX: 5.5, CHI: 3.5, MIA: 0.5, LON: 9, TOR: 3.5 },
  'atlanta': { NYC: 2.5, LAX: 4.5, CHI: 2, MIA: 2, LON: 9, TOR: 2.5 },
  'charleston': { NYC: 2, LAX: 5, CHI: 2.5, MIA: 2, LON: 8.5, TOR: 2.5 },
  'savannah': { NYC: 2, LAX: 5, CHI: 2.5, MIA: 1.5, LON: 9, TOR: 2.5 },
  'nashville': { NYC: 2.5, LAX: 4, CHI: 1.5, MIA: 2.5, LON: 9, TOR: 2 },
  'new orleans': { NYC: 3, LAX: 4, CHI: 2.5, MIA: 2, LON: 10, TOR: 3 },

  // Southwest
  'arizona': { NYC: 5, LAX: 1.5, CHI: 3.5, MIA: 5, LON: 11, TOR: 5 },
  'phoenix': { NYC: 5, LAX: 1.5, CHI: 3.5, MIA: 5, LON: 11, TOR: 5 },
  'scottsdale': { NYC: 5, LAX: 1.5, CHI: 3.5, MIA: 5, LON: 11, TOR: 5 },
  'sedona': { NYC: 5, LAX: 1.5, CHI: 4, MIA: 5, LON: 11, TOR: 5 },
  'tucson': { NYC: 5, LAX: 1.5, CHI: 4, MIA: 5, LON: 11, TOR: 5 },
  'utah': { NYC: 5, LAX: 2, CHI: 4, MIA: 5.5, LON: 11, TOR: 5 },
  'salt lake': { NYC: 5, LAX: 2, CHI: 4, MIA: 5.5, LON: 10, TOR: 5 },
  'moab': { NYC: 5.5, LAX: 2, CHI: 4, MIA: 6, LON: 11, TOR: 5.5 },
  'page': { NYC: 5.5, LAX: 2, CHI: 4.5, MIA: 6, LON: 11, TOR: 5.5 },
  'las vegas': { NYC: 5, LAX: 1, CHI: 4, MIA: 5, LON: 10, TOR: 5 },
  'vegas': { NYC: 5, LAX: 1, CHI: 4, MIA: 5, LON: 10, TOR: 5 },

  // Texas
  'texas': { NYC: 4, LAX: 3.5, CHI: 3, MIA: 3, LON: 10, TOR: 4 },
  'austin': { NYC: 4, LAX: 3.5, CHI: 3, MIA: 3, LON: 10, TOR: 4 },
  'dallas': { NYC: 4, LAX: 3.5, CHI: 2.5, MIA: 3, LON: 10, TOR: 3.5 },
  'houston': { NYC: 4, LAX: 3.5, CHI: 3, MIA: 3, LON: 10, TOR: 4 },
  'san antonio': { NYC: 4, LAX: 3, CHI: 3, MIA: 3, LON: 10, TOR: 4 },

  // Mountain/West
  'colorado': { NYC: 4.5, LAX: 2.5, CHI: 3, MIA: 5, LON: 10, TOR: 4.5 },
  'denver': { NYC: 4.5, LAX: 2.5, CHI: 3, MIA: 5, LON: 10, TOR: 4.5 },
  'aspen': { NYC: 5, LAX: 3, CHI: 3.5, MIA: 5.5, LON: 11, TOR: 5 },
  'montana': { NYC: 5.5, LAX: 3, CHI: 4, MIA: 6, LON: 11, TOR: 5 },
  'wyoming': { NYC: 5, LAX: 2.5, CHI: 3.5, MIA: 5.5, LON: 11, TOR: 5 },
  'yellowstone': { NYC: 5.5, LAX: 3, CHI: 4, MIA: 6, LON: 11, TOR: 5 },
  'idaho': { NYC: 5.5, LAX: 2.5, CHI: 4, MIA: 6, LON: 11, TOR: 5 },
  'new mexico': { NYC: 5, LAX: 2, CHI: 3.5, MIA: 5, LON: 11, TOR: 5 },
  'santa fe': { NYC: 5, LAX: 2.5, CHI: 3.5, MIA: 5, LON: 11, TOR: 5 },

  // West Coast
  'california': { NYC: 6, LAX: 0, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },
  'los angeles': { NYC: 6, LAX: 0, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },
  'la': { NYC: 6, LAX: 0, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },
  'san francisco': { NYC: 6, LAX: 1.5, CHI: 4.5, MIA: 6, LON: 11, TOR: 5.5 },
  'san diego': { NYC: 6, LAX: 0.5, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },
  'napa': { NYC: 6, LAX: 1.5, CHI: 4.5, MIA: 6, LON: 11, TOR: 5.5 },
  'malibu': { NYC: 6, LAX: 0, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },
  'big sur': { NYC: 6, LAX: 1, CHI: 5, MIA: 6, LON: 11, TOR: 6 },
  'carmel': { NYC: 6, LAX: 1, CHI: 5, MIA: 6, LON: 11, TOR: 6 },
  'palm springs': { NYC: 5.5, LAX: 0.5, CHI: 4.5, MIA: 5.5, LON: 11, TOR: 5.5 },

  // Pacific Northwest
  'seattle': { NYC: 6, LAX: 2.5, CHI: 4.5, MIA: 6, LON: 10, TOR: 5 },
  'portland': { NYC: 6, LAX: 2.5, CHI: 4.5, MIA: 6, LON: 10, TOR: 5 },
  'oregon': { NYC: 6, LAX: 2.5, CHI: 4.5, MIA: 6, LON: 10, TOR: 5 },
  'washington state': { NYC: 6, LAX: 2.5, CHI: 4.5, MIA: 6, LON: 10, TOR: 5 },

  // Hawaii
  'hawaii': { NYC: 11, LAX: 5.5, CHI: 9, MIA: 10, LON: 17, TOR: 10 },
  'honolulu': { NYC: 11, LAX: 5.5, CHI: 9, MIA: 10, LON: 17, TOR: 10 },
  'maui': { NYC: 11, LAX: 5.5, CHI: 9, MIA: 10, LON: 17, TOR: 10 },
  'oahu': { NYC: 11, LAX: 5.5, CHI: 9, MIA: 10, LON: 17, TOR: 10 },
  'kauai': { NYC: 11, LAX: 6, CHI: 9.5, MIA: 10.5, LON: 18, TOR: 10.5 },
  'big island': { NYC: 11, LAX: 6, CHI: 9.5, MIA: 10.5, LON: 18, TOR: 10.5 },

  // Alaska
  'alaska': { NYC: 8, LAX: 5, CHI: 7, MIA: 9, LON: 12, TOR: 7 },
  'anchorage': { NYC: 8, LAX: 5, CHI: 7, MIA: 9, LON: 12, TOR: 7 },

  // ==================== CANADA ====================
  'toronto': { NYC: 1.5, LAX: 5, CHI: 2, MIA: 3, LON: 8, TOR: 0 },
  'vancouver': { NYC: 6, LAX: 3, CHI: 4.5, MIA: 6.5, LON: 10, TOR: 5 },
  'montreal': { NYC: 1.5, LAX: 6, CHI: 2.5, MIA: 3.5, LON: 7, TOR: 1 },
  'quebec': { NYC: 2, LAX: 6.5, CHI: 3, MIA: 4, LON: 7, TOR: 1.5 },
  'calgary': { NYC: 5, LAX: 3, CHI: 4, MIA: 6, LON: 9, TOR: 4 },
  'banff': { NYC: 5.5, LAX: 3.5, CHI: 4.5, MIA: 6.5, LON: 10, TOR: 4.5 },
  'whistler': { NYC: 6.5, LAX: 3.5, CHI: 5, MIA: 7, LON: 11, TOR: 5.5 },

  // ==================== CARIBBEAN ====================
  'bahamas': { NYC: 3, LAX: 6, CHI: 4, MIA: 1, LON: 9, TOR: 3.5 },
  'nassau': { NYC: 3, LAX: 6, CHI: 4, MIA: 1, LON: 9, TOR: 3.5 },
  'turks': { NYC: 3.5, LAX: 6.5, CHI: 4.5, MIA: 1.5, LON: 9.5, TOR: 4 },
  'caicos': { NYC: 3.5, LAX: 6.5, CHI: 4.5, MIA: 1.5, LON: 9.5, TOR: 4 },
  'jamaica': { NYC: 4, LAX: 6, CHI: 4.5, MIA: 2, LON: 10, TOR: 4 },
  'montego bay': { NYC: 4, LAX: 6, CHI: 4.5, MIA: 2, LON: 10, TOR: 4 },
  'aruba': { NYC: 4.5, LAX: 7, CHI: 5, MIA: 3, LON: 10, TOR: 5 },
  'curacao': { NYC: 5, LAX: 7, CHI: 5.5, MIA: 3.5, LON: 10, TOR: 5.5 },
  'st lucia': { NYC: 4.5, LAX: 7, CHI: 5.5, MIA: 3.5, LON: 9, TOR: 5 },
  'barbados': { NYC: 4.5, LAX: 8, CHI: 6, MIA: 4, LON: 9, TOR: 5 },
  'virgin islands': { NYC: 4, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'st thomas': { NYC: 4, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'puerto rico': { NYC: 3.5, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'san juan': { NYC: 3.5, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'dominican': { NYC: 4, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'punta cana': { NYC: 4, LAX: 7, CHI: 5, MIA: 2.5, LON: 9, TOR: 4.5 },
  'cuba': { NYC: 4, LAX: 6, CHI: 4, MIA: 1, LON: 10, TOR: 4 },
  'havana': { NYC: 4, LAX: 6, CHI: 4, MIA: 1, LON: 10, TOR: 4 },
  'cayman': { NYC: 4, LAX: 5.5, CHI: 4, MIA: 1.5, LON: 10, TOR: 4 },
  'bermuda': { NYC: 2, LAX: 7, CHI: 3.5, MIA: 2.5, LON: 7, TOR: 3 },
  'antigua': { NYC: 4.5, LAX: 8, CHI: 6, MIA: 4, LON: 8.5, TOR: 5 },
  'st barts': { NYC: 4.5, LAX: 8, CHI: 6, MIA: 4, LON: 8.5, TOR: 5 },
  'st kitts': { NYC: 4, LAX: 7.5, CHI: 5.5, MIA: 3.5, LON: 9, TOR: 5 },
  'grenada': { NYC: 5, LAX: 8, CHI: 6, MIA: 4, LON: 9, TOR: 5.5 },
  'martinique': { NYC: 4.5, LAX: 8, CHI: 6, MIA: 4, LON: 8.5, TOR: 5 },
  'guadeloupe': { NYC: 4.5, LAX: 8, CHI: 6, MIA: 4, LON: 8.5, TOR: 5 },

  // ==================== MEXICO ====================
  'mexico': { NYC: 5, LAX: 4, CHI: 4.5, MIA: 3, LON: 11, TOR: 5 },
  'mexico city': { NYC: 5, LAX: 4, CHI: 4.5, MIA: 3, LON: 11, TOR: 5 },
  'cancun': { NYC: 4, LAX: 5, CHI: 4, MIA: 2, LON: 10, TOR: 4.5 },
  'tulum': { NYC: 4, LAX: 5, CHI: 4, MIA: 2, LON: 10, TOR: 4.5 },
  'riviera maya': { NYC: 4, LAX: 5, CHI: 4, MIA: 2, LON: 10, TOR: 4.5 },
  'playa del carmen': { NYC: 4, LAX: 5, CHI: 4, MIA: 2, LON: 10, TOR: 4.5 },
  'cabo': { NYC: 6, LAX: 2.5, CHI: 5, MIA: 5, LON: 12, TOR: 6 },
  'los cabos': { NYC: 6, LAX: 2.5, CHI: 5, MIA: 5, LON: 12, TOR: 6 },
  'puerto vallarta': { NYC: 5.5, LAX: 3, CHI: 4.5, MIA: 4.5, LON: 12, TOR: 5.5 },
  'guadalajara': { NYC: 5, LAX: 3.5, CHI: 4.5, MIA: 4, LON: 12, TOR: 5 },
  'oaxaca': { NYC: 5.5, LAX: 4.5, CHI: 5, MIA: 4, LON: 12, TOR: 5.5 },
  'merida': { NYC: 4, LAX: 5, CHI: 4, MIA: 2, LON: 11, TOR: 4.5 },

  // ==================== CENTRAL AMERICA ====================
  'costa rica': { NYC: 5, LAX: 6, CHI: 5, MIA: 3.5, LON: 11, TOR: 5.5 },
  'san jose costa rica': { NYC: 5, LAX: 6, CHI: 5, MIA: 3.5, LON: 11, TOR: 5.5 },
  'belize': { NYC: 4.5, LAX: 5.5, CHI: 4.5, MIA: 2.5, LON: 11, TOR: 5 },
  'guatemala': { NYC: 5, LAX: 5.5, CHI: 4.5, MIA: 3, LON: 11, TOR: 5 },
  'panama': { NYC: 5.5, LAX: 7, CHI: 5.5, MIA: 4, LON: 11, TOR: 6 },
  'panama city': { NYC: 5.5, LAX: 7, CHI: 5.5, MIA: 4, LON: 11, TOR: 6 },
  'honduras': { NYC: 4.5, LAX: 6, CHI: 4.5, MIA: 3, LON: 11, TOR: 5 },
  'nicaragua': { NYC: 5, LAX: 6, CHI: 5, MIA: 3.5, LON: 11, TOR: 5.5 },
  'el salvador': { NYC: 5, LAX: 5.5, CHI: 4.5, MIA: 3.5, LON: 11, TOR: 5.5 },

  // ==================== SOUTH AMERICA ====================
  'colombia': { NYC: 5.5, LAX: 7, CHI: 6, MIA: 4, LON: 11, TOR: 6 },
  'bogota': { NYC: 5.5, LAX: 7, CHI: 6, MIA: 4, LON: 11, TOR: 6 },
  'cartagena': { NYC: 5, LAX: 7, CHI: 5.5, MIA: 3.5, LON: 11, TOR: 5.5 },
  'medellin': { NYC: 5.5, LAX: 7, CHI: 6, MIA: 4, LON: 11, TOR: 6 },
  'peru': { NYC: 8, LAX: 9, CHI: 8, MIA: 6, LON: 13, TOR: 8.5 },
  'lima': { NYC: 8, LAX: 9, CHI: 8, MIA: 6, LON: 13, TOR: 8.5 },
  'cusco': { NYC: 9, LAX: 10, CHI: 9, MIA: 7, LON: 14, TOR: 9.5 },
  'machu picchu': { NYC: 9, LAX: 10, CHI: 9, MIA: 7, LON: 14, TOR: 9.5 },
  'ecuador': { NYC: 6, LAX: 8, CHI: 7, MIA: 5, LON: 12, TOR: 7 },
  'quito': { NYC: 6, LAX: 8, CHI: 7, MIA: 5, LON: 12, TOR: 7 },
  'galapagos': { NYC: 8, LAX: 9, CHI: 8, MIA: 6, LON: 14, TOR: 8.5 },
  'brazil': { NYC: 10, LAX: 13, CHI: 11, MIA: 8.5, LON: 12, TOR: 10.5 },
  'rio': { NYC: 10, LAX: 13, CHI: 11, MIA: 8.5, LON: 12, TOR: 10.5 },
  'rio de janeiro': { NYC: 10, LAX: 13, CHI: 11, MIA: 8.5, LON: 12, TOR: 10.5 },
  'sao paulo': { NYC: 10, LAX: 13, CHI: 11, MIA: 8.5, LON: 12, TOR: 10.5 },
  'argentina': { NYC: 11, LAX: 13, CHI: 12, MIA: 9.5, LON: 14, TOR: 12 },
  'buenos aires': { NYC: 11, LAX: 13, CHI: 12, MIA: 9.5, LON: 14, TOR: 12 },
  'patagonia': { NYC: 13, LAX: 15, CHI: 14, MIA: 12, LON: 16, TOR: 14 },
  'chile': { NYC: 11, LAX: 12, CHI: 12, MIA: 10, LON: 15, TOR: 12 },
  'santiago': { NYC: 11, LAX: 12, CHI: 12, MIA: 10, LON: 15, TOR: 12 },
  'uruguay': { NYC: 11, LAX: 14, CHI: 12, MIA: 10, LON: 14, TOR: 12 },
  'montevideo': { NYC: 11, LAX: 14, CHI: 12, MIA: 10, LON: 14, TOR: 12 },
  'venezuela': { NYC: 5, LAX: 8, CHI: 6, MIA: 4, LON: 10, TOR: 6 },
  'bolivia': { NYC: 10, LAX: 12, CHI: 11, MIA: 9, LON: 15, TOR: 11 },

  // ==================== WESTERN EUROPE ====================
  'london': { NYC: 7, LAX: 11, CHI: 8.5, MIA: 9, LON: 0, TOR: 7.5 },
  'england': { NYC: 7, LAX: 11, CHI: 8.5, MIA: 9, LON: 0, TOR: 7.5 },
  'uk': { NYC: 7, LAX: 11, CHI: 8.5, MIA: 9, LON: 0, TOR: 7.5 },
  'scotland': { NYC: 7.5, LAX: 11, CHI: 9, MIA: 9.5, LON: 1.5, TOR: 8 },
  'edinburgh': { NYC: 7.5, LAX: 11, CHI: 9, MIA: 9.5, LON: 1.5, TOR: 8 },
  'ireland': { NYC: 7, LAX: 11, CHI: 8.5, MIA: 9, LON: 1.5, TOR: 7 },
  'dublin': { NYC: 7, LAX: 11, CHI: 8.5, MIA: 9, LON: 1.5, TOR: 7 },
  'paris': { NYC: 7.5, LAX: 11, CHI: 9, MIA: 9.5, LON: 1, TOR: 8 },
  'france': { NYC: 7.5, LAX: 11, CHI: 9, MIA: 9.5, LON: 1, TOR: 8 },
  'nice': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'provence': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'bordeaux': { NYC: 8.5, LAX: 12, CHI: 10, MIA: 10, LON: 1.5, TOR: 8.5 },
  'amsterdam': { NYC: 8, LAX: 11, CHI: 9, MIA: 10, LON: 1, TOR: 8 },
  'netherlands': { NYC: 8, LAX: 11, CHI: 9, MIA: 10, LON: 1, TOR: 8 },
  'belgium': { NYC: 8, LAX: 11, CHI: 9, MIA: 10, LON: 1, TOR: 8 },
  'brussels': { NYC: 8, LAX: 11, CHI: 9, MIA: 10, LON: 1, TOR: 8 },
  'germany': { NYC: 8.5, LAX: 11.5, CHI: 9.5, MIA: 10, LON: 2, TOR: 8.5 },
  'berlin': { NYC: 9, LAX: 12, CHI: 10, MIA: 10.5, LON: 2, TOR: 9 },
  'munich': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'frankfurt': { NYC: 8, LAX: 11.5, CHI: 9, MIA: 9.5, LON: 1.5, TOR: 8 },
  'switzerland': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'zurich': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'geneva': { NYC: 9, LAX: 12, CHI: 10, MIA: 10, LON: 1.5, TOR: 9 },
  'austria': { NYC: 9, LAX: 12, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'vienna': { NYC: 9, LAX: 12, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'salzburg': { NYC: 9.5, LAX: 12.5, CHI: 10.5, MIA: 11, LON: 2, TOR: 10 },

  // ==================== SOUTHERN EUROPE ====================
  'spain': { NYC: 8, LAX: 12, CHI: 10, MIA: 9, LON: 2.5, TOR: 8.5 },
  'barcelona': { NYC: 8, LAX: 12, CHI: 10, MIA: 9.5, LON: 2, TOR: 8.5 },
  'madrid': { NYC: 8, LAX: 12, CHI: 10, MIA: 9, LON: 2.5, TOR: 8.5 },
  'seville': { NYC: 9, LAX: 12.5, CHI: 10.5, MIA: 9.5, LON: 2.5, TOR: 9 },
  'malaga': { NYC: 9, LAX: 12.5, CHI: 10.5, MIA: 9.5, LON: 3, TOR: 9 },
  'ibiza': { NYC: 9, LAX: 12.5, CHI: 10.5, MIA: 10, LON: 2.5, TOR: 9.5 },
  'mallorca': { NYC: 9, LAX: 12.5, CHI: 10.5, MIA: 10, LON: 2.5, TOR: 9.5 },
  'portugal': { NYC: 7, LAX: 11.5, CHI: 9, MIA: 8.5, LON: 2.5, TOR: 7.5 },
  'lisbon': { NYC: 7, LAX: 11.5, CHI: 9, MIA: 8.5, LON: 2.5, TOR: 7.5 },
  'porto': { NYC: 7, LAX: 11.5, CHI: 9, MIA: 8.5, LON: 2.5, TOR: 7.5 },
  'algarve': { NYC: 8, LAX: 12.5, CHI: 10, MIA: 9, LON: 3, TOR: 8.5 },
  'italy': { NYC: 9, LAX: 13, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'rome': { NYC: 9, LAX: 13, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'florence': { NYC: 9, LAX: 13, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'venice': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 10.5, LON: 2.5, TOR: 10 },
  'milan': { NYC: 9, LAX: 12.5, CHI: 10, MIA: 10, LON: 2, TOR: 9 },
  'naples': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'amalfi': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'positano': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'sicily': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'sardinia': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'cinque terre': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 10.5, LON: 2.5, TOR: 10 },
  'tuscany': { NYC: 9, LAX: 13, CHI: 10, MIA: 10.5, LON: 2.5, TOR: 9.5 },
  'lake como': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 10.5, LON: 2, TOR: 10 },
  'greece': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 4, TOR: 10.5 },
  'athens': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 4, TOR: 10.5 },
  'santorini': { NYC: 11, LAX: 15, CHI: 12, MIA: 12, LON: 4.5, TOR: 11.5 },
  'mykonos': { NYC: 11, LAX: 15, CHI: 12, MIA: 12, LON: 4.5, TOR: 11.5 },
  'crete': { NYC: 11, LAX: 15, CHI: 12, MIA: 12, LON: 4.5, TOR: 11.5 },
  'rhodes': { NYC: 11, LAX: 15, CHI: 12, MIA: 12, LON: 4.5, TOR: 11.5 },
  'corfu': { NYC: 11, LAX: 14.5, CHI: 12, MIA: 11.5, LON: 4, TOR: 11 },
  'croatia': { NYC: 10, LAX: 13.5, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'dubrovnik': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'split': { NYC: 10, LAX: 13.5, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'montenegro': { NYC: 10.5, LAX: 14, CHI: 11.5, MIA: 11.5, LON: 3.5, TOR: 11 },
  'slovenia': { NYC: 10, LAX: 13.5, CHI: 11, MIA: 11, LON: 2.5, TOR: 10.5 },
  'malta': { NYC: 10, LAX: 14, CHI: 11, MIA: 11, LON: 3, TOR: 10.5 },
  'cyprus': { NYC: 11, LAX: 15, CHI: 12, MIA: 12, LON: 4.5, TOR: 11.5 },

  // ==================== NORTHERN & EASTERN EUROPE ====================
  'norway': { NYC: 9, LAX: 12, CHI: 10, MIA: 11, LON: 2, TOR: 9 },
  'oslo': { NYC: 9, LAX: 12, CHI: 10, MIA: 11, LON: 2, TOR: 9 },
  'bergen': { NYC: 9.5, LAX: 12.5, CHI: 10.5, MIA: 11.5, LON: 2, TOR: 9.5 },
  'sweden': { NYC: 9, LAX: 12, CHI: 10, MIA: 11, LON: 2, TOR: 9 },
  'stockholm': { NYC: 9, LAX: 12, CHI: 10, MIA: 11, LON: 2.5, TOR: 9 },
  'denmark': { NYC: 8.5, LAX: 12, CHI: 9.5, MIA: 10.5, LON: 2, TOR: 8.5 },
  'copenhagen': { NYC: 8.5, LAX: 12, CHI: 9.5, MIA: 10.5, LON: 2, TOR: 8.5 },
  'finland': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11.5, LON: 3, TOR: 10 },
  'helsinki': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11.5, LON: 3, TOR: 10 },
  'iceland': { NYC: 5.5, LAX: 9, CHI: 7, MIA: 8, LON: 3, TOR: 6 },
  'reykjavik': { NYC: 5.5, LAX: 9, CHI: 7, MIA: 8, LON: 3, TOR: 6 },
  'poland': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11, LON: 2.5, TOR: 10 },
  'warsaw': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11, LON: 2.5, TOR: 10 },
  'krakow': { NYC: 10, LAX: 13.5, CHI: 11, MIA: 11.5, LON: 2.5, TOR: 10.5 },
  'czech': { NYC: 9, LAX: 12.5, CHI: 10, MIA: 10.5, LON: 2, TOR: 9.5 },
  'prague': { NYC: 9, LAX: 12.5, CHI: 10, MIA: 10.5, LON: 2, TOR: 9.5 },
  'hungary': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11, LON: 2.5, TOR: 10 },
  'budapest': { NYC: 9.5, LAX: 13, CHI: 10.5, MIA: 11, LON: 2.5, TOR: 10 },
  'romania': { NYC: 11, LAX: 14, CHI: 12, MIA: 12, LON: 3.5, TOR: 11.5 },
  'turkey': { NYC: 11, LAX: 14, CHI: 12, MIA: 12, LON: 4, TOR: 11.5 },
  'istanbul': { NYC: 11, LAX: 14, CHI: 12, MIA: 12, LON: 4, TOR: 11.5 },
  'cappadocia': { NYC: 12, LAX: 15, CHI: 13, MIA: 13, LON: 5, TOR: 12.5 },
  'russia': { NYC: 10, LAX: 13, CHI: 11, MIA: 12, LON: 4, TOR: 10.5 },
  'moscow': { NYC: 10, LAX: 13, CHI: 11, MIA: 12, LON: 4, TOR: 10.5 },

  // ==================== MIDDLE EAST ====================
  'dubai': { NYC: 14, LAX: 16, CHI: 15, MIA: 15, LON: 7, TOR: 14.5 },
  'uae': { NYC: 14, LAX: 16, CHI: 15, MIA: 15, LON: 7, TOR: 14.5 },
  'abu dhabi': { NYC: 14, LAX: 16, CHI: 15, MIA: 15, LON: 7, TOR: 14.5 },
  'qatar': { NYC: 14, LAX: 16, CHI: 15, MIA: 15, LON: 7, TOR: 14.5 },
  'doha': { NYC: 14, LAX: 16, CHI: 15, MIA: 15, LON: 7, TOR: 14.5 },
  'israel': { NYC: 11, LAX: 15, CHI: 12.5, MIA: 12, LON: 5, TOR: 12 },
  'tel aviv': { NYC: 11, LAX: 15, CHI: 12.5, MIA: 12, LON: 5, TOR: 12 },
  'jerusalem': { NYC: 11.5, LAX: 15, CHI: 13, MIA: 12.5, LON: 5, TOR: 12.5 },
  'jordan': { NYC: 12, LAX: 15.5, CHI: 13, MIA: 13, LON: 5, TOR: 12.5 },
  'petra': { NYC: 12.5, LAX: 16, CHI: 13.5, MIA: 13.5, LON: 5.5, TOR: 13 },
  'oman': { NYC: 15, LAX: 17, CHI: 16, MIA: 16, LON: 8, TOR: 15.5 },
  'saudi arabia': { NYC: 13, LAX: 16, CHI: 14, MIA: 14, LON: 6, TOR: 13.5 },
  'egypt': { NYC: 11, LAX: 15, CHI: 12.5, MIA: 12, LON: 5, TOR: 12 },
  'cairo': { NYC: 11, LAX: 15, CHI: 12.5, MIA: 12, LON: 5, TOR: 12 },

  // ==================== AFRICA ====================
  'morocco': { NYC: 8, LAX: 13, CHI: 10, MIA: 10, LON: 3.5, TOR: 9 },
  'marrakech': { NYC: 8, LAX: 13, CHI: 10, MIA: 10, LON: 3.5, TOR: 9 },
  'casablanca': { NYC: 8, LAX: 13, CHI: 10, MIA: 10, LON: 3.5, TOR: 9 },
  'fez': { NYC: 8.5, LAX: 13.5, CHI: 10.5, MIA: 10.5, LON: 4, TOR: 9.5 },
  'south africa': { NYC: 18, LAX: 20, CHI: 19, MIA: 17, LON: 11, TOR: 18.5 },
  'cape town': { NYC: 18, LAX: 20, CHI: 19, MIA: 17, LON: 11, TOR: 18.5 },
  'johannesburg': { NYC: 17, LAX: 19, CHI: 18, MIA: 16, LON: 11, TOR: 17.5 },
  'kruger': { NYC: 18, LAX: 20, CHI: 19, MIA: 17, LON: 12, TOR: 18.5 },
  'kenya': { NYC: 17, LAX: 20, CHI: 18, MIA: 17, LON: 9, TOR: 17.5 },
  'nairobi': { NYC: 17, LAX: 20, CHI: 18, MIA: 17, LON: 9, TOR: 17.5 },
  'masai mara': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'tanzania': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'serengeti': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'zanzibar': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'kilimanjaro': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'rwanda': { NYC: 18, LAX: 21, CHI: 19, MIA: 18, LON: 10, TOR: 18.5 },
  'botswana': { NYC: 19, LAX: 21, CHI: 20, MIA: 18, LON: 11, TOR: 19.5 },
  'victoria falls': { NYC: 19, LAX: 21, CHI: 20, MIA: 18, LON: 12, TOR: 19.5 },
  'zambia': { NYC: 19, LAX: 21, CHI: 20, MIA: 18, LON: 12, TOR: 19.5 },
  'zimbabwe': { NYC: 19, LAX: 21, CHI: 20, MIA: 18, LON: 12, TOR: 19.5 },
  'namibia': { NYC: 18, LAX: 20, CHI: 19, MIA: 17, LON: 11, TOR: 18.5 },
  'mauritius': { NYC: 21, LAX: 23, CHI: 22, MIA: 20, LON: 12, TOR: 21.5 },
  'seychelles': { NYC: 19, LAX: 22, CHI: 20, MIA: 19, LON: 10, TOR: 19.5 },
  'madagascar': { NYC: 22, LAX: 24, CHI: 23, MIA: 21, LON: 14, TOR: 22.5 },
  'ghana': { NYC: 11, LAX: 15, CHI: 13, MIA: 12, LON: 6.5, TOR: 12 },
  'senegal': { NYC: 8, LAX: 14, CHI: 11, MIA: 10, LON: 6, TOR: 9.5 },
  'nigeria': { NYC: 12, LAX: 16, CHI: 14, MIA: 13, LON: 6.5, TOR: 13 },
  'ethiopia': { NYC: 16, LAX: 19, CHI: 17, MIA: 16, LON: 8, TOR: 16.5 },

  // ==================== ASIA - EAST ====================
  'japan': { NYC: 14, LAX: 11, CHI: 13, MIA: 17, LON: 12, TOR: 13 },
  'tokyo': { NYC: 14, LAX: 11, CHI: 13, MIA: 17, LON: 12, TOR: 13 },
  'kyoto': { NYC: 14.5, LAX: 12, CHI: 13.5, MIA: 17.5, LON: 12.5, TOR: 13.5 },
  'osaka': { NYC: 14, LAX: 11.5, CHI: 13, MIA: 17, LON: 12, TOR: 13 },
  'hiroshima': { NYC: 15, LAX: 12.5, CHI: 14, MIA: 18, LON: 13, TOR: 14 },
  'hokkaido': { NYC: 15, LAX: 12, CHI: 14, MIA: 18, LON: 13, TOR: 14 },
  'okinawa': { NYC: 16, LAX: 13, CHI: 15, MIA: 19, LON: 14, TOR: 15 },
  'china': { NYC: 14, LAX: 12, CHI: 13.5, MIA: 17, LON: 10, TOR: 14 },
  'beijing': { NYC: 14, LAX: 12, CHI: 13.5, MIA: 17, LON: 10, TOR: 14 },
  'shanghai': { NYC: 15, LAX: 12.5, CHI: 14, MIA: 18, LON: 11, TOR: 14.5 },
  'hong kong': { NYC: 16, LAX: 14, CHI: 15.5, MIA: 18, LON: 12, TOR: 15.5 },
  'taiwan': { NYC: 16, LAX: 13, CHI: 15.5, MIA: 19, LON: 13, TOR: 15.5 },
  'taipei': { NYC: 16, LAX: 13, CHI: 15.5, MIA: 19, LON: 13, TOR: 15.5 },
  'korea': { NYC: 14, LAX: 12, CHI: 13.5, MIA: 17, LON: 11, TOR: 13.5 },
  'seoul': { NYC: 14, LAX: 12, CHI: 13.5, MIA: 17, LON: 11, TOR: 13.5 },

  // ==================== ASIA - SOUTHEAST ====================
  'thailand': { NYC: 18, LAX: 17, CHI: 18, MIA: 20, LON: 11, TOR: 18 },
  'bangkok': { NYC: 18, LAX: 17, CHI: 18, MIA: 20, LON: 11, TOR: 18 },
  'phuket': { NYC: 19, LAX: 18, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'chiang mai': { NYC: 19, LAX: 18, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'koh samui': { NYC: 19, LAX: 18, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'vietnam': { NYC: 18, LAX: 16, CHI: 18, MIA: 20, LON: 12, TOR: 18 },
  'hanoi': { NYC: 18, LAX: 16, CHI: 18, MIA: 20, LON: 12, TOR: 18 },
  'ho chi minh': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'saigon': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'singapore': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 13, TOR: 19 },
  'malaysia': { NYC: 20, LAX: 17, CHI: 19.5, MIA: 21, LON: 13, TOR: 19.5 },
  'kuala lumpur': { NYC: 20, LAX: 17, CHI: 19.5, MIA: 21, LON: 13, TOR: 19.5 },
  'langkawi': { NYC: 20, LAX: 18, CHI: 20, MIA: 22, LON: 13, TOR: 20 },
  'indonesia': { NYC: 21, LAX: 18, CHI: 20, MIA: 22, LON: 15, TOR: 20 },
  'bali': { NYC: 21, LAX: 18, CHI: 20, MIA: 22, LON: 15, TOR: 20 },
  'jakarta': { NYC: 21, LAX: 18, CHI: 20, MIA: 22, LON: 15, TOR: 20 },
  'philippines': { NYC: 17, LAX: 14, CHI: 17, MIA: 20, LON: 14, TOR: 17 },
  'manila': { NYC: 17, LAX: 14, CHI: 17, MIA: 20, LON: 14, TOR: 17 },
  'palawan': { NYC: 18, LAX: 15, CHI: 18, MIA: 21, LON: 15, TOR: 18 },
  'cambodia': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 12, TOR: 19 },
  'siem reap': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 13, TOR: 19 },
  'angkor wat': { NYC: 19, LAX: 17, CHI: 19, MIA: 21, LON: 13, TOR: 19 },
  'laos': { NYC: 20, LAX: 18, CHI: 20, MIA: 22, LON: 13, TOR: 20 },
  'myanmar': { NYC: 20, LAX: 18, CHI: 20, MIA: 22, LON: 13, TOR: 20 },
  'brunei': { NYC: 20, LAX: 17, CHI: 20, MIA: 22, LON: 15, TOR: 20 },

  // ==================== ASIA - SOUTH ====================
  'india': { NYC: 16, LAX: 18, CHI: 17, MIA: 18, LON: 9, TOR: 16 },
  'delhi': { NYC: 16, LAX: 18, CHI: 17, MIA: 18, LON: 9, TOR: 16 },
  'mumbai': { NYC: 17, LAX: 19, CHI: 18, MIA: 18, LON: 9, TOR: 17 },
  'goa': { NYC: 18, LAX: 20, CHI: 19, MIA: 19, LON: 10, TOR: 18 },
  'jaipur': { NYC: 17, LAX: 19, CHI: 18, MIA: 19, LON: 10, TOR: 17 },
  'kerala': { NYC: 19, LAX: 21, CHI: 20, MIA: 20, LON: 11, TOR: 19 },
  'rajasthan': { NYC: 17, LAX: 19, CHI: 18, MIA: 19, LON: 10, TOR: 17 },
  'maldives': { NYC: 18, LAX: 20, CHI: 19, MIA: 19, LON: 10, TOR: 18 },
  'sri lanka': { NYC: 19, LAX: 21, CHI: 20, MIA: 20, LON: 11, TOR: 19 },
  'nepal': { NYC: 17, LAX: 19, CHI: 18, MIA: 19, LON: 11, TOR: 17 },
  'kathmandu': { NYC: 17, LAX: 19, CHI: 18, MIA: 19, LON: 11, TOR: 17 },
  'bhutan': { NYC: 18, LAX: 20, CHI: 19, MIA: 20, LON: 12, TOR: 18 },
  'pakistan': { NYC: 15, LAX: 17, CHI: 16, MIA: 17, LON: 8, TOR: 15 },
  'bangladesh': { NYC: 18, LAX: 20, CHI: 19, MIA: 20, LON: 11, TOR: 18 },

  // ==================== OCEANIA ====================
  'australia': { NYC: 22, LAX: 15, CHI: 20, MIA: 22, LON: 22, TOR: 21 },
  'sydney': { NYC: 22, LAX: 15, CHI: 20, MIA: 22, LON: 22, TOR: 21 },
  'melbourne': { NYC: 22, LAX: 15.5, CHI: 20.5, MIA: 22, LON: 22, TOR: 21 },
  'brisbane': { NYC: 21, LAX: 14.5, CHI: 19.5, MIA: 21, LON: 22, TOR: 20 },
  'perth': { NYC: 23, LAX: 18, CHI: 22, MIA: 24, LON: 18, TOR: 22 },
  'cairns': { NYC: 20, LAX: 14, CHI: 18.5, MIA: 20, LON: 21, TOR: 19 },
  'gold coast': { NYC: 21, LAX: 14.5, CHI: 19.5, MIA: 21, LON: 22, TOR: 20 },
  'great barrier reef': { NYC: 20, LAX: 14, CHI: 18.5, MIA: 20, LON: 21, TOR: 19 },
  'tasmania': { NYC: 23, LAX: 16, CHI: 21, MIA: 23, LON: 23, TOR: 22 },
  'new zealand': { NYC: 24, LAX: 13, CHI: 21, MIA: 23, LON: 24, TOR: 22 },
  'auckland': { NYC: 24, LAX: 13, CHI: 21, MIA: 23, LON: 24, TOR: 22 },
  'queenstown': { NYC: 24, LAX: 14, CHI: 22, MIA: 24, LON: 25, TOR: 23 },
  'fiji': { NYC: 20, LAX: 11, CHI: 17, MIA: 19, LON: 22, TOR: 18 },
  'tahiti': { NYC: 14, LAX: 8, CHI: 12, MIA: 14, LON: 20, TOR: 13 },
  'french polynesia': { NYC: 14, LAX: 8, CHI: 12, MIA: 14, LON: 20, TOR: 13 },
  'bora bora': { NYC: 15, LAX: 9, CHI: 13, MIA: 15, LON: 21, TOR: 14 },
  'cook islands': { NYC: 16, LAX: 10, CHI: 14, MIA: 16, LON: 22, TOR: 15 },
  'samoa': { NYC: 18, LAX: 11, CHI: 16, MIA: 18, LON: 23, TOR: 17 },
  'vanuatu': { NYC: 22, LAX: 15, CHI: 20, MIA: 22, LON: 23, TOR: 21 },
  'papua new guinea': { NYC: 22, LAX: 16, CHI: 20, MIA: 22, LON: 22, TOR: 21 },
  'guam': { NYC: 18, LAX: 10, CHI: 16, MIA: 20, LON: 17, TOR: 17 },

  // ==================== DRIVEABLE FROM NYC (return 0 for flight) ====================
  'berkshire': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'lenox': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'hudson valley': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'catskills': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'finger lakes': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'poconos': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'cape cod': { NYC: 0, LAX: 6.5, CHI: 3.5, MIA: 4, LON: 7.5, TOR: 2 },
  'martha': { NYC: 0, LAX: 6.5, CHI: 3.5, MIA: 4, LON: 7.5, TOR: 2 },
  'nantucket': { NYC: 0, LAX: 6.5, CHI: 3.5, MIA: 4, LON: 7.5, TOR: 2 },
  'hamptons': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
  'woodstock': { NYC: 0, LAX: 6, CHI: 3, MIA: 3.5, LON: 8, TOR: 2 },
};

/**
 * Maps home city aliases to one of 6 hub keys (NYC, LAX, CHI, MIA, LON, TOR).
 * Cities not explicitly listed here will cause getStaticFlightEstimate to return null,
 * triggering an LLM fallback.
 */
export const HOME_CITY_MAP = {
  'nyc': 'NYC', 'new york': 'NYC', 'new york city': 'NYC',
  'la': 'LAX', 'los angeles': 'LAX', 'lax': 'LAX',
  'chicago': 'CHI', 'chi': 'CHI',
  'miami': 'MIA', 'mia': 'MIA',
  'london': 'LON', 'uk': 'LON', 'england': 'LON',
  'toronto': 'TOR', 'tor': 'TOR',
  // Default other cities to nearest hub
  'boston': 'NYC', 'philadelphia': 'NYC', 'dc': 'NYC', 'washington': 'NYC',
  'san francisco': 'LAX', 'seattle': 'LAX', 'portland': 'LAX', 'san diego': 'LAX',
  'dallas': 'CHI', 'houston': 'CHI', 'denver': 'CHI', 'atlanta': 'CHI',
  'orlando': 'MIA', 'tampa': 'MIA',
  'vancouver': 'TOR', 'montreal': 'TOR',
};

/**
 * Look up estimated flight hours from a user's home city to a destination.
 *
 * Uses exact match first, then word-boundary matching for short keys (<=3 chars)
 * to prevent substring collisions (e.g. 'la' matching 'auckland').
 *
 * @param {string} destinationName - The destination city/region name.
 * @param {string} [homeCity=''] - The user's home city.
 * @returns {number|null} Estimated flight hours, or null if no match (triggers LLM fallback).
 */
export function getStaticFlightEstimate(destinationName, homeCity = '') {
  if (!destinationName) return null;
  const dest = destinationName.toLowerCase();
  const home = homeCity.toLowerCase();

  // Map home city to standard key — no NYC fallback
  const homeKey = HOME_CITY_MAP[home] || null;
  if (!homeKey) return null; // Unknown home city -> trigger LLM fallback

  // BUG 1 FIX: Use exact match first, then word-boundary matching to prevent
  // short keys like 'la' matching "auckland", 'uk' matching "bangkok", etc.
  // Phase 1: Exact match (destination IS the keyword)
  if (FLIGHT_TIMES[dest]) {
    return FLIGHT_TIMES[dest][homeKey] || FLIGHT_TIMES[dest].NYC;
  }

  // Phase 2: Word-boundary matching for multi-word destinations
  // Short keywords (<=3 chars) require word boundary match to prevent substring collisions
  for (const [keyword, times] of Object.entries(FLIGHT_TIMES)) {
    if (keyword.length <= 3) {
      // Short keys: require word boundary (e.g., 'la' must not match 'auckland')
      const regex = new RegExp(`\\b${keyword}\\b`);
      if (regex.test(dest)) {
        return times[homeKey] || times.NYC;
      }
    } else {
      // Longer keys: substring match is safe (e.g., 'patagonia' won't false-match)
      if (dest.includes(keyword)) {
        return times[homeKey] || times.NYC;
      }
    }
  }

  // No match found
  return null;
}

// ─── City-to-City Flight Duration Heuristics ────────────────────────────────

/** Cross-continental flight duration estimates. */
export const CROSS_CONTINENTAL_DURATIONS = {
  'South America-Europe': '~12h', 'Europe-South America': '~12h',
  'North America-Europe': '~8h', 'Europe-North America': '~8h',
  'North America-Asia': '~13h', 'Asia-North America': '~13h',
  'South America-Asia': '~24h', 'Asia-South America': '~24h',
  'North America-Africa': '~12h', 'Africa-North America': '~12h',
  'South America-Africa': '~11h', 'Africa-South America': '~11h',
  'North America-Oceania': '~14h', 'Oceania-North America': '~14h',
  'South America-Oceania': '~15h', 'Oceania-South America': '~15h',
  'South America-North America': '~10h', 'North America-South America': '~10h',
  'Europe-Asia': '~11h', 'Asia-Europe': '~11h',
  'Europe-Africa': '~5h', 'Africa-Europe': '~5h',
  'Europe-Oceania': '~22h', 'Oceania-Europe': '~22h',
  'Asia-Africa': '~10h', 'Africa-Asia': '~10h',
  'Asia-Oceania': '~9h', 'Oceania-Asia': '~9h',
  'Africa-Oceania': '~16h', 'Oceania-Africa': '~16h',
};

/** Large countries where domestic flights can be very long. */
export const LARGE_COUNTRY_DOMESTIC_MAX = {
  'Australia': '~5h', 'United States': '~6h', 'USA': '~6h',
  'Canada': '~6h', 'Russia': '~9h', 'Brazil': '~5h',
  'China': '~5h', 'India': '~4h', 'Indonesia': '~5h',
  'Argentina': '~4h', 'Chile': '~4h', 'Mexico': '~4h',
  'Japan': '~3h', 'South Africa': '~3h', 'Nigeria': '~2h',
};

/** Same-continent different-country duration estimates. */
export const LARGE_CONTINENT_ESTIMATES = {
  'Africa': '~6h', 'Asia': '~6h', 'Europe': '~4h',
  'North America': '~5h', 'South America': '~5h', 'Oceania': '~4h',
};

/** Static city-to-city flight duration map (120+ common routes). */
export const STATIC_FLIGHT_DURATIONS = {
  // Argentina - Patagonia routes
  'buenos aires-el calafate': '3h 15m', 'el calafate-buenos aires': '3h 15m',
  'buenos aires-ushuaia': '3h 30m', 'ushuaia-buenos aires': '3h 30m',
  'buenos aires-bariloche': '2h', 'bariloche-buenos aires': '2h',
  'el calafate-ushuaia': '1h 15m', 'ushuaia-el calafate': '1h 15m',
  'el calafate-bariloche': '1h 45m', 'bariloche-el calafate': '1h 45m',
  // Chile
  'santiago-punta arenas': '3h 30m', 'punta arenas-santiago': '3h 30m',
  'santiago-puerto natales': '4h', 'puerto natales-santiago': '4h',
  // South America internal
  'buenos aires-santiago': '2h', 'santiago-buenos aires': '2h',
  'lima-cusco': '1h 15m', 'cusco-lima': '1h 15m',
  'buenos aires-lima': '4h', 'lima-buenos aires': '4h',
  // South America to Europe
  'buenos aires-madrid': '12h', 'madrid-buenos aires': '12h',
  'santiago-madrid': '13h', 'madrid-santiago': '13h',
  'lima-madrid': '12h', 'madrid-lima': '12h',
  'sao paulo-lisbon': '10h', 'lisbon-sao paulo': '10h',
  // South America to Oceania
  'buenos aires-auckland': '14h', 'auckland-buenos aires': '14h',
  'santiago-auckland': '12h', 'auckland-santiago': '12h',
  'buenos aires-sydney': '15h', 'sydney-buenos aires': '15h',
  // Europe internal
  'paris-london': '1h', 'london-paris': '1h',
  'rome-paris': '2h', 'paris-rome': '2h',
  'barcelona-madrid': '1h 15m', 'madrid-barcelona': '1h 15m',
  'lyon-lisbon': '2h 30m', 'lisbon-lyon': '2h 30m',
  'lyon-london': '1h 45m', 'london-lyon': '1h 45m',
  'lyon-rome': '1h 30m', 'rome-lyon': '1h 30m',
  'lyon-barcelona': '1h 30m', 'barcelona-lyon': '1h 30m',
  'lyon-madrid': '2h', 'madrid-lyon': '2h',
  'lyon-paris': '1h', 'paris-lyon': '1h',
  'lisbon-london': '2h 30m', 'london-lisbon': '2h 30m',
  'lisbon-paris': '2h 30m', 'paris-lisbon': '2h 30m',
  'lisbon-rome': '3h', 'rome-lisbon': '3h',
  'lisbon-barcelona': '2h', 'barcelona-lisbon': '2h',
  'lisbon-madrid': '1h', 'madrid-lisbon': '1h',
  'porto-florence': '2h 30m', 'florence-porto': '2h 30m',
  'porto-rome': '3h', 'rome-porto': '3h',
  'porto-milan': '2h 30m', 'milan-porto': '2h 30m',
  'porto-barcelona': '2h', 'barcelona-porto': '2h',
  'porto-madrid': '1h 15m', 'madrid-porto': '1h 15m',
  'porto-paris': '2h', 'paris-porto': '2h',
  'porto-london': '2h 30m', 'london-porto': '2h 30m',
  'porto-amsterdam': '2h 45m', 'amsterdam-porto': '2h 45m',
  'porto-lisbon': '1h', 'lisbon-porto': '1h',
  'porto-tuscany': '2h 30m', 'tuscany-porto': '2h 30m',
  'porto-fiesole': '2h 30m', 'fiesole-porto': '2h 30m',
  'florence-london': '2h 15m', 'london-florence': '2h 15m',
  'florence-paris': '2h', 'paris-florence': '2h',
  'florence-barcelona': '2h', 'barcelona-florence': '2h',
  'florence-madrid': '2h 30m', 'madrid-florence': '2h 30m',
  'florence-amsterdam': '2h', 'amsterdam-florence': '2h',
  'florence-lisbon': '3h', 'lisbon-florence': '3h',
  'rome-london': '2h 30m', 'london-rome': '2h 30m',
  'rome-barcelona': '2h', 'barcelona-rome': '2h',
  'rome-madrid': '2h 30m', 'madrid-rome': '2h 30m',
  'london-madrid': '2h 30m', 'madrid-london': '2h 30m',
  'london-barcelona': '2h', 'barcelona-london': '2h',
  'paris-barcelona': '2h', 'barcelona-paris': '2h',
  'paris-madrid': '2h', 'madrid-paris': '2h',
  'amsterdam-london': '1h', 'london-amsterdam': '1h',
  'amsterdam-paris': '1h 15m', 'paris-amsterdam': '1h 15m',
  'amsterdam-barcelona': '2h 15m', 'barcelona-amsterdam': '2h 15m',
  'amsterdam-rome': '2h 15m', 'rome-amsterdam': '2h 15m',
  'amsterdam-lisbon': '3h', 'lisbon-amsterdam': '3h',
  'berlin-london': '2h', 'london-berlin': '2h',
  'berlin-paris': '1h 45m', 'paris-berlin': '1h 45m',
  'berlin-rome': '2h', 'rome-berlin': '2h',
  'berlin-barcelona': '2h 45m', 'barcelona-berlin': '2h 45m',
  'athens-rome': '2h', 'rome-athens': '2h',
  'athens-london': '3h 30m', 'london-athens': '3h 30m',
  'athens-paris': '3h', 'paris-athens': '3h',
  'dublin-london': '1h 15m', 'london-dublin': '1h 15m',
  'dublin-paris': '1h 45m', 'paris-dublin': '1h 45m',
  'vienna-london': '2h 30m', 'london-vienna': '2h 30m',
  'vienna-paris': '2h', 'paris-vienna': '2h',
  'vienna-rome': '1h 30m', 'rome-vienna': '1h 30m',
  'prague-london': '2h', 'london-prague': '2h',
  'prague-paris': '1h 45m', 'paris-prague': '1h 45m',
  'copenhagen-london': '2h', 'london-copenhagen': '2h',
  'istanbul-london': '4h', 'london-istanbul': '3h 30m',
  'istanbul-paris': '3h 30m', 'paris-istanbul': '3h 30m',
  'istanbul-rome': '2h 30m', 'rome-istanbul': '2h 30m',
  // North America internal
  'new york-los angeles': '5h 30m', 'los angeles-new york': '5h',
  'new york-miami': '3h', 'miami-new york': '3h',
  'new york-chicago': '2h 30m', 'chicago-new york': '2h 30m',
  'los angeles-san francisco': '1h 15m', 'san francisco-los angeles': '1h 15m',
  // Transatlantic
  'new york-london': '7h', 'london-new york': '8h',
  'new york-paris': '8h', 'paris-new york': '9h',
  'nyc-london': '7h', 'london-nyc': '8h',
  'nyc-paris': '8h', 'paris-nyc': '9h',
  // Asia
  'tokyo-osaka': '1h', 'osaka-tokyo': '1h',
  'bangkok-tokyo': '6h', 'tokyo-bangkok': '7h',
  'tokyo-naha': '2h 30m', 'naha-tokyo': '2h 30m',
  'tokyo-okinawa': '2h 30m', 'okinawa-tokyo': '2h 30m',
  'osaka-naha': '2h', 'naha-osaka': '2h',
  'osaka-okinawa': '2h', 'okinawa-osaka': '2h',
  'kyoto-naha': '2h', 'naha-kyoto': '2h',
  'fukuoka-naha': '1h 30m', 'naha-fukuoka': '1h 30m',
  'tokyo-sapporo': '1h 30m', 'sapporo-tokyo': '1h 30m',
  'osaka-sapporo': '2h', 'sapporo-osaka': '2h',
  'sapporo-kyoto': '2h', 'kyoto-sapporo': '2h',
  'tokyo-niseko': '2h', 'niseko-tokyo': '2h',
  'osaka-niseko': '2h 15m', 'niseko-osaka': '2h 15m',
  'kyoto-niseko': '2h 15m', 'niseko-kyoto': '2h 15m',
  // North America — more domestic
  'chicago-denver': '2h 30m', 'denver-chicago': '3h',
  'chicago-miami': '3h 30m', 'miami-chicago': '3h 30m',
  'chicago-los angeles': '4h 30m', 'los angeles-chicago': '4h',
  'new york-denver': '4h 30m', 'denver-new york': '4h',
  'new york-seattle': '5h 30m', 'seattle-new york': '5h',
  'new york-san francisco': '6h', 'san francisco-new york': '5h 30m',
  'new york-dallas': '3h 30m', 'dallas-new york': '3h 30m',
  'new york-atlanta': '2h 30m', 'atlanta-new york': '2h 30m',
  'new york-boston': '1h 15m', 'boston-new york': '1h 15m',
  'los angeles-seattle': '2h 30m', 'seattle-los angeles': '2h 30m',
  'los angeles-honolulu': '5h 30m', 'honolulu-los angeles': '5h',
  'toronto-new york': '1h 30m', 'new york-toronto': '1h 30m',
  'toronto-vancouver': '5h', 'vancouver-toronto': '4h 30m',
  'toronto-montreal': '1h', 'montreal-toronto': '1h',
  // Middle East from Europe
  'london-dubai': '7h', 'dubai-london': '7h 30m',
  'paris-dubai': '6h 30m', 'dubai-paris': '7h',
  'london-doha': '7h', 'doha-london': '7h 30m',
  'london-istanbul': '3h 30m', 'istanbul-london': '4h',
  'paris-istanbul': '3h 30m', 'istanbul-paris': '3h 30m',
  // Middle East from NA
  'new york-dubai': '13h', 'dubai-new york': '14h',
  'new york-doha': '13h', 'doha-new york': '14h',
  // Africa from Europe/NA
  'london-cape town': '11h 30m', 'cape town-london': '11h',
  'london-nairobi': '8h 30m', 'nairobi-london': '8h 30m',
  'london-johannesburg': '11h', 'johannesburg-london': '11h',
  'new york-johannesburg': '16h', 'johannesburg-new york': '16h',
  // Oceania
  'sydney-auckland': '3h 30m', 'auckland-sydney': '3h',
  'sydney-melbourne': '1h 30m', 'melbourne-sydney': '1h 30m',
  'sydney-perth': '5h', 'perth-sydney': '5h',
  'sydney-brisbane': '1h 30m', 'brisbane-sydney': '1h 30m',
  // SE Asia internal
  'bangkok-singapore': '2h 30m', 'singapore-bangkok': '2h 30m',
  'bangkok-bali': '4h', 'bali-bangkok': '4h',
  'singapore-bali': '2h 30m', 'bali-singapore': '2h 30m',
  'bangkok-ho chi minh': '1h 30m', 'ho chi minh-bangkok': '1h 30m',
  'singapore-kuala lumpur': '1h', 'kuala lumpur-singapore': '1h',
  // New Zealand internal
  'auckland-queenstown': '2h', 'queenstown-auckland': '2h',
  'auckland-wellington': '1h', 'wellington-auckland': '1h',
};

/**
 * Estimate flight duration between two cities using geography-aware heuristics.
 * Falls through: static route map → cross-continental → large-country domestic → same-continent.
 *
 * @param {string} fromCity - Origin city
 * @param {string} toCity - Destination city
 * @param {Object} helpers - { getCityCountrySync, getContinentForCountry }
 * @returns {string} Duration string like "2h 30m", "~8h", etc.
 */
/**
 * Convert great-circle distance to a realistic commercial flight duration
 * (door-to-door minus airport time). Uses curve-fit numbers from real
 * scheduled times — short hops have proportionally more taxi/climb
 * overhead, long-haul averages ~525 mph.
 *
 * @param {number} miles Great-circle distance
 * @returns {string} Duration string like "5h 30m"
 */
function flightDurationFromMiles(miles) {
  if (!miles || miles <= 0) return null;
  let mins;
  if (miles <= 250) {
    // Short hop — taxi + climb + descend dominates
    mins = 60 + miles * 0.12;
  } else if (miles <= 600) {
    // Regional jet
    mins = 75 + (miles - 250) * 0.105;
  } else if (miles <= 1500) {
    // Domestic narrowbody (~480 mph cruise + overhead)
    mins = 113 + (miles - 600) * 0.115;
  } else if (miles <= 3500) {
    // Transcontinental / mid-haul (~510 mph)
    mins = 217 + (miles - 1500) * 0.118;
  } else {
    // Long-haul widebody (~525 mph + 1 connection buffer for ultra-long)
    mins = 453 + (miles - 3500) * 0.114;
  }
  mins = Math.round(mins);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function estimateFlightDurationHeuristic(fromCity, toCity, helpers = {}) {
  const {
    getCityCountrySync: getCountry,
    getContinentForCountry: getContinent,
    // Optional coords helper — passed by determineInterCityTransport when
    // available so we can use Haversine instead of country/continent
    // defaults. If absent, we fall through to the legacy logic below.
    getCityCoords: getCoords,
    greatCircleMiles: gcm,
  } = helpers;

  let fromLower = (fromCity || '').toLowerCase().trim();
  let toLower = (toCity || '').toLowerCase().trim();

  // 1. Static route map (exact city pairs) — wins because it includes
  // commercial-schedule realities the haversine formula can't know
  // (jet stream asymmetry, hub-and-spoke connections, etc.).
  const routeKey = `${fromLower}-${toLower}`;
  if (STATIC_FLIGHT_DURATIONS[routeKey]) {
    return STATIC_FLIGHT_DURATIONS[routeKey];
  }

  // 1b. Resort/hotel name normalization — if city name looks like a resort
  // (long name with hospitality keywords), try to extract a known destination word.
  // e.g., "Centara Ras Fushi Resort" → check each word → "maldives" not found,
  // but "Four Seasons Bali" → "bali" found in FLIGHT_TIMES.
  const RESORT_SUFFIXES = /\b(resort|hotel|spa|lodge|villa|palace|suites?|retreat|hideaway|sanctuary|private island)\b/i;
  const normalizeResortCity = (name) => {
    if (!name || name.length < 10 || !RESORT_SUFFIXES.test(name)) return name;
    // Strip common resort suffixes and split into words
    const stripped = name.replace(RESORT_SUFFIXES, '').replace(/\s+/g, ' ').trim();
    const words = stripped.split(/[\s,&]+/).filter(w => w.length > 2);
    // Try each word against FLIGHT_TIMES keys
    for (const word of words) {
      if (FLIGHT_TIMES[word]) return word;
    }
    return name;
  };

  const fromNorm = normalizeResortCity(fromLower);
  const toNorm = normalizeResortCity(toLower);
  if (fromNorm !== fromLower || toNorm !== toLower) {
    // Retry with normalized names
    const normRouteKey = `${fromNorm}-${toNorm}`;
    if (STATIC_FLIGHT_DURATIONS[normRouteKey]) {
      return STATIC_FLIGHT_DURATIONS[normRouteKey];
    }
    // Update for subsequent lookups
    fromLower = fromNorm;
    toLower = toNorm;
  }

  // 1b. Dynamic distance-based estimate. If the caller provided coords
  // helpers AND we can resolve both endpoints, this gives a realistic
  // duration for ANY city pair in the world — no need to add hard-coded
  // routes for every NYC↔X. Falls through to country/continent defaults
  // when coords aren't available.
  //
  // Airport-code fallback: when the input is an IATA code ("JFK", "MEX")
  // getCoords returns null. Resolve the airport code to a city via the
  // optional reverseLookupAirportCity helper (passed by callers that have
  // access to transportConstants), then retry coord lookup. This is the
  // fix for the user-reported "JFK → MEX showed 1.5h" bug — the heuristic
  // was previously falling through to the ~4h continental default because
  // getCoords('JFK') is null.
  if (typeof getCoords === 'function' && typeof gcm === 'function') {
    const reverseLookup = helpers.reverseLookupAirportCity;
    const looksLikeIATA = (s) => typeof s === 'string' && /^[A-Z]{3}$/i.test(s.trim());
    const resolveCoords = (rawInput, lowerInput) => {
      let coords = getCoords(lowerInput) || getCoords(rawInput);
      if (!coords && typeof reverseLookup === 'function' && looksLikeIATA(rawInput)) {
        const cityName = reverseLookup(rawInput.trim().toUpperCase());
        if (cityName) {
          coords = getCoords(cityName.toLowerCase()) || getCoords(cityName);
        }
      }
      return coords;
    };
    const fromCoords = resolveCoords(fromCity, fromLower);
    const toCoords = resolveCoords(toCity, toLower);
    if (fromCoords && toCoords) {
      const miles = gcm(fromCoords, toCoords);
      const dyn = flightDurationFromMiles(miles);
      if (dyn) return dyn;
    }
  }

  if (!getCountry || !getContinent) return '~4h';

  // Use normalized names for country/continent lookups
  const fromCountry = getCountry(fromLower) || getCountry(fromCity);
  const toCountry = getCountry(toLower) || getCountry(toCity);
  const fromContinent = getContinent(fromCountry);
  const toContinent = getContinent(toCountry);

  // 2. Cross-continental estimates (last-resort default when coords missing)
  if (fromContinent && toContinent && fromContinent !== toContinent) {
    const key = `${fromContinent}-${toContinent}`;
    return CROSS_CONTINENTAL_DURATIONS[key] || '~12h';
  }

  // 3. Same country — use country-specific domestic estimate
  if (fromCountry && toCountry && fromCountry === toCountry) {
    return LARGE_COUNTRY_DOMESTIC_MAX[fromCountry] || '~2h';
  }

  // 4. Same continent, different countries
  if (fromCountry && toCountry && fromCountry !== toCountry) {
    const continent = fromContinent || toContinent;
    return LARGE_CONTINENT_ESTIMATES[continent] || '~4h';
  }

  return '~4h';
}

// ─── Schedule Estimation (departure/arrival times) ────────────────────────────
// Based on real commercial scheduling patterns: curfews, connection banks,
// jet stream asymmetry, dateline crossings, and hub rotation windows.

/**
 * Parse a duration string like "3h 15m", "~8h", "7h", "1h 30m" to minutes.
 */
function parseDurationToMinutes(dur) {
  if (!dur) return 240;
  const hMatch = dur.match(/(\d+\.?\d*)\s*h/i);
  const mMatch = dur.match(/(\d+)\s*m/i);
  const hoursMatch = dur.match(/(\d+\.?\d*)\s*hours?/i);
  if (hMatch) return Math.round(parseFloat(hMatch[1]) * 60) + (mMatch ? parseInt(mMatch[1]) : 0);
  if (hoursMatch) return Math.round(parseFloat(hoursMatch[1]) * 60);
  if (mMatch) return parseInt(mMatch[1]);
  return 240;
}

// ─── Route Schedule Rules ─────────────────────────────────────────────────────
// Each rule: { depart: "HH:MM", arrive_day_offset, flight_type }
// Departure is midpoint of typical commercial scheduling window.
// Times in local time at origin (departure) and destination (arrival).

const ROUTE_RULES = [
  // NA → Europe (eastbound) — evening departure, morning +1d arrival
  { from: 'North America', to: 'Europe', depart: '20:00', window: [17, 23], dayOffset: 1, type: 'overnight' },
  // Europe → NA (westbound) — morning departure, same-day afternoon arrival
  { from: 'Europe', to: 'North America', depart: '11:00', window: [9, 13], dayOffset: 0, type: 'daylight' },
  // NA West Coast → East Asia — midday departure, +1d afternoon arrival
  { from: 'North America', to: 'East Asia', subFrom: 'west', depart: '12:30', window: [11, 14], dayOffset: 1, type: 'daylight' },
  // NA East Coast → East Asia — polar routing, afternoon departure, +1d early AM
  { from: 'North America', to: 'East Asia', subFrom: 'east', depart: '14:00', window: [1, 16], dayOffset: 1, type: 'polar' },
  // East Asia → NA West Coast — evening departure, same-day morning (dateline gain)
  { from: 'East Asia', to: 'North America', subTo: 'west', depart: '17:30', window: [16, 19], dayOffset: 0, type: 'daylight' },
  // East Asia → NA East Coast — late morning departure, same-day midday
  { from: 'East Asia', to: 'North America', subTo: 'east', depart: '11:30', window: [10, 13], dayOffset: 0, type: 'daylight' },
  // NA → Southeast Asia — late morning departure, +1d evening arrival
  { from: 'North America', to: 'Southeast Asia', depart: '10:00', window: [8, 1], dayOffset: 1, type: 'ultra-long-haul' },
  // Southeast Asia → NA — late night departure, same-day morning (dateline gain)
  { from: 'Southeast Asia', to: 'North America', depart: '00:15', window: [23, 2], dayOffset: 0, type: 'ultra-long-haul' },
  // NA → South Asia — evening departure, +1d evening arrival (polar)
  { from: 'North America', to: 'South Asia', depart: '20:30', window: [19, 22], dayOffset: 1, type: 'ultra-long-haul' },
  // South Asia → NA — early AM departure, same-day morning
  { from: 'South Asia', to: 'North America', depart: '01:45', window: [0, 3], dayOffset: 0, type: 'ultra-long-haul' },
  // NA East → SA East — overnight red-eye
  { from: 'North America', to: 'South America', subTo: 'east', depart: '22:00', window: [21, 0], dayOffset: 1, type: 'overnight' },
  // SA East → NA East — overnight
  { from: 'South America', to: 'North America', subFrom: 'east', depart: '22:30', window: [21, 0], dayOffset: 1, type: 'overnight' },
  // NA → SA West Coast — morning departure, same-day evening
  { from: 'North America', to: 'South America', subTo: 'west', depart: '09:00', window: [8, 23], dayOffset: 0, type: 'medium-long-haul' },
  // SA West → NA — late night, same-day morning
  { from: 'South America', to: 'North America', subFrom: 'west', depart: '00:00', window: [23, 2], dayOffset: 0, type: 'overnight' },
  // NA → Central America/Caribbean — morning departure, same day
  { from: 'North America', to: 'Central America', depart: '10:00', window: [6, 16], dayOffset: 0, type: 'short-haul' },
  // NA → Oceania — late night, +2d morning (dateline)
  { from: 'North America', to: 'Oceania', depart: '22:00', window: [21, 0], dayOffset: 2, type: 'ultra-long-haul' },
  // Oceania → NA — midday departure, same-day morning (dateline gain)
  { from: 'Oceania', to: 'North America', depart: '12:30', window: [10, 15], dayOffset: 0, type: 'daylight' },
  // NA → North/West Africa — evening overnight
  { from: 'North America', to: 'Africa', depart: '22:00', window: [21, 0], dayOffset: 1, type: 'overnight' },
  // Africa → NA — evening overnight
  { from: 'Africa', to: 'North America', depart: '22:00', window: [21, 0], dayOffset: 1, type: 'overnight' },
  // Europe → East Asia (day flight) — late morning, +1d early AM
  { from: 'Europe', to: 'East Asia', depart: '11:30', window: [10, 22], dayOffset: 1, type: 'day flight' },
  // East Asia → Europe (day flight) — late morning, same-day afternoon
  { from: 'East Asia', to: 'Europe', depart: '11:30', window: [10, 1], dayOffset: 0, type: 'daylight' },
  // Europe → Southeast Asia — evening, +1d morning
  { from: 'Europe', to: 'Southeast Asia', depart: '21:00', window: [10, 0], dayOffset: 1, type: 'long-haul' },
  // Southeast Asia → Europe — late night, same-day morning
  { from: 'Southeast Asia', to: 'Europe', depart: '00:15', window: [23, 2], dayOffset: 0, type: 'overnight' },
  // Europe → South Asia — evening, +1d pre-dawn/morning
  { from: 'Europe', to: 'South Asia', depart: '21:00', window: [9, 23], dayOffset: 1, type: 'long-haul' },
  // South Asia → Europe — early AM, same-day morning
  { from: 'South Asia', to: 'Europe', depart: '02:30', window: [2, 15], dayOffset: 0, type: 'long-haul' },
  // Europe → SA East — evening, +1d morning
  { from: 'Europe', to: 'South America', depart: '19:30', window: [17, 22], dayOffset: 1, type: 'overnight' },
  // SA East → Europe — evening, +1d late morning
  { from: 'South America', to: 'Europe', depart: '19:30', window: [17, 22], dayOffset: 1, type: 'overnight' },
  // Europe → Middle East — midday, same-day evening
  { from: 'Europe', to: 'Middle East', depart: '12:30', window: [9, 16], dayOffset: 0, type: 'medium-haul' },
  // Middle East → Europe — morning, same-day morning/midday
  { from: 'Middle East', to: 'Europe', depart: '09:00', window: [2, 16], dayOffset: 0, type: 'medium-haul' },
  // Europe → North Africa — morning, same day
  { from: 'Europe', to: 'North Africa', depart: '10:00', window: [7, 17], dayOffset: 0, type: 'short-haul' },
  // Europe → East Africa — evening, +1d morning
  { from: 'Europe', to: 'East Africa', depart: '21:00', window: [20, 22], dayOffset: 1, type: 'overnight' },
  // Europe → Southern Africa — evening, +1d morning
  { from: 'Europe', to: 'Southern Africa', depart: '20:00', window: [19, 22], dayOffset: 1, type: 'overnight' },
  // Africa → Europe — late night, same-day morning
  { from: 'Africa', to: 'Europe', depart: '23:00', window: [22, 1], dayOffset: 0, type: 'overnight' },
  // East Asia → Oceania — evening, +1d morning
  { from: 'East Asia', to: 'Oceania', depart: '21:30', window: [20, 23], dayOffset: 1, type: 'overnight' },
  // Oceania → East Asia — morning/midday, same-day evening
  { from: 'Oceania', to: 'East Asia', depart: '10:00', window: [8, 0], dayOffset: 0, type: 'long-haul' },
  // Southeast Asia → Oceania — late night, +1d morning
  { from: 'Southeast Asia', to: 'Oceania', depart: '23:00', window: [22, 1], dayOffset: 1, type: 'overnight' },
  // Oceania → Southeast Asia — midday, same-day evening
  { from: 'Oceania', to: 'Southeast Asia', depart: '14:30', window: [13, 16], dayOffset: 0, type: 'long-haul' },
  // East Asia → Middle East — late night, same-day early morning
  { from: 'East Asia', to: 'Middle East', depart: '00:15', window: [23, 2], dayOffset: 0, type: 'overnight' },
  // Middle East → East Asia — early morning, same-day afternoon/evening
  { from: 'Middle East', to: 'East Asia', depart: '03:00', window: [2, 10], dayOffset: 0, type: 'long-haul' },
  // Southeast Asia → Middle East — evening, same-day late night
  { from: 'Southeast Asia', to: 'Middle East', depart: '20:30', window: [19, 22], dayOffset: 0, type: 'long-haul' },
  // South Asia → East Africa — early AM, same-day morning
  { from: 'South Asia', to: 'East Africa', depart: '03:30', window: [2, 5], dayOffset: 0, type: 'medium-haul' },
  // Africa → Middle East — late night, +1d morning
  { from: 'Africa', to: 'Middle East', depart: '00:00', window: [22, 2], dayOffset: 1, type: 'overnight' },
  // Middle East → Africa — early AM, same-day morning
  { from: 'Middle East', to: 'Africa', depart: '06:00', window: [2, 10], dayOffset: 0, type: 'medium-haul' },
  // South America → Oceania — late night, +2d morning (dateline)
  { from: 'South America', to: 'Oceania', depart: '23:30', window: [23, 1], dayOffset: 2, type: 'ultra-long-haul' },
  // Oceania → South America — midday, same-day late morning (dateline gain)
  { from: 'Oceania', to: 'South America', depart: '14:00', window: [13, 15], dayOffset: 0, type: 'ultra-long-haul' },
  // Africa (JNB) → South America — morning, same-day afternoon
  { from: 'Africa', to: 'South America', depart: '10:00', window: [9, 11], dayOffset: 0, type: 'long-haul' },
  // South America → Africa — evening, +1d morning
  { from: 'South America', to: 'Africa', depart: '19:00', window: [18, 20], dayOffset: 1, type: 'overnight' },
];

// ─── Region Classification ────────────────────────────────────────────────────

// City-name hints for sub-region detection (east/west coast, specific regions)
const EAST_COAST_NA = new Set(['new york', 'nyc', 'boston', 'miami', 'atlanta', 'washington', 'dc',
  'philadelphia', 'orlando', 'charlotte', 'tampa', 'fort lauderdale', 'palm beach',
  'charleston', 'savannah', 'jacksonville', 'pittsburgh', 'nashville', 'new orleans',
  'toronto', 'montreal', 'halifax', 'st johns']);
const WEST_COAST_NA = new Set(['los angeles', 'la', 'san francisco', 'seattle', 'portland',
  'san diego', 'las vegas', 'sacramento', 'vancouver', 'honolulu', 'anchorage',
  // Mountain West — closer to West Coast for flight routing
  'utah', 'salt lake city', 'salt lake', 'denver', 'colorado', 'phoenix', 'scottsdale',
  'sedona', 'tucson', 'arizona', 'aspen', 'moab', 'page', 'jackson hole', 'montana',
  'boise', 'albuquerque', 'santa fe', 'amangiri']);
const EAST_ASIA = new Set(['tokyo', 'kyoto', 'osaka', 'seoul', 'busan', 'beijing', 'shanghai',
  'hong kong', 'taipei', 'sapporo', 'fukuoka', 'nagoya', 'hiroshima', 'okinawa',
  'guangzhou', 'macau', 'jeju']);
const SOUTHEAST_ASIA = new Set(['bangkok', 'singapore', 'kuala lumpur', 'bali', 'jakarta',
  'manila', 'ho chi minh', 'hanoi', 'da nang', 'hoi an', 'chiang mai', 'phuket',
  'koh samui', 'krabi', 'siem reap', 'phnom penh', 'cebu', 'palawan', 'penang',
  'langkawi', 'lombok']);
const SOUTH_ASIA = new Set(['mumbai', 'delhi', 'jaipur', 'goa', 'bangalore', 'kolkata',
  'chennai', 'colombo', 'kathmandu', 'kerala', 'hyderabad', 'maldives', 'male']);
const EAST_SA = new Set(['rio de janeiro', 'sao paulo', 'buenos aires', 'salvador']);
const WEST_SA = new Set(['lima', 'cusco', 'bogota', 'medellin', 'santiago', 'quito']);
const CENTRAL_AMERICA = new Set(['cancun', 'tulum', 'playa del carmen', 'mexico city',
  'oaxaca', 'puerto vallarta', 'guadalajara', 'los cabos', 'cabo san lucas',
  'san jose', 'panama city', 'havana', 'san juan', 'nassau', 'kingston', 'punta cana']);
const NORTH_AFRICA = new Set(['cairo', 'luxor', 'marrakech', 'fes', 'casablanca']);
const EAST_AFRICA = new Set(['nairobi', 'dar es salaam', 'zanzibar', 'addis ababa', 'kigali',
  'masai mara', 'serengeti', 'victoria falls', 'kilimanjaro', 'arusha', 'ngorongoro']);
const SOUTHERN_AFRICA = new Set(['cape town', 'johannesburg', 'windhoek', 'okavango delta',
  'garden route', 'stellenbosch', 'kruger', 'sabi sand']);
const MIDDLE_EAST = new Set(['dubai', 'abu dhabi', 'doha', 'riyadh', 'jeddah']);
const OCEANIA = new Set(['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide',
  'auckland', 'queenstown', 'wellington', 'christchurch', 'fiji', 'nadi', 'tahiti', 'bora bora']);

/**
 * Classify a city into a region + sub-region for route matching.
 * Falls back to continent from helpers if city isn't in the Sets above.
 */
function classifyCityRegion(cityName, helpers = {}) {
  if (!cityName) return { region: null, sub: null };
  const city = cityName.toLowerCase().trim();
  const { getCityCountrySync: getCountry, getContinentForCountry: getContinent } = helpers;

  // Helper: check a single token against all region Sets
  const checkRegionSets = (token) => {
    if (EAST_COAST_NA.has(token)) return { region: 'North America', sub: 'east' };
    if (WEST_COAST_NA.has(token)) return { region: 'North America', sub: 'west' };
    if (EAST_ASIA.has(token)) return { region: 'East Asia', sub: null };
    if (SOUTHEAST_ASIA.has(token)) return { region: 'Southeast Asia', sub: null };
    if (SOUTH_ASIA.has(token)) return { region: 'South Asia', sub: null };
    if (EAST_SA.has(token)) return { region: 'South America', sub: 'east' };
    if (WEST_SA.has(token)) return { region: 'South America', sub: 'west' };
    if (CENTRAL_AMERICA.has(token)) return { region: 'Central America', sub: null };
    if (NORTH_AFRICA.has(token)) return { region: 'North Africa', sub: null };
    if (EAST_AFRICA.has(token)) return { region: 'East Africa', sub: null };
    if (SOUTHERN_AFRICA.has(token)) return { region: 'Southern Africa', sub: null };
    if (MIDDLE_EAST.has(token)) return { region: 'Middle East', sub: null };
    if (OCEANIA.has(token)) return { region: 'Oceania', sub: null };
    return null;
  };

  // 1. Try full city name first
  const fullMatch = checkRegionSets(city);
  if (fullMatch) return fullMatch;

  // 2. Try comma-separated parts (e.g. "Canyon Point, Utah" → try "canyon point", "utah")
  if (city.includes(',')) {
    const parts = city.split(',').map(p => p.trim()).filter(Boolean);
    for (const part of parts) {
      const partMatch = checkRegionSets(part);
      if (partMatch) return partMatch;
    }
  }

  // 3. Fallback: use continent from helpers
  if (getCountry && getContinent) {
    const country = getCountry(cityName);
    const continent = getContinent(country);
    if (continent) {
      const cl = continent.toLowerCase();
      if (cl.includes('north america')) return { region: 'North America', sub: null };
      if (cl.includes('south america')) return { region: 'South America', sub: null };
      if (cl.includes('europe')) return { region: 'Europe', sub: null };
      if (cl.includes('asia')) return { region: 'East Asia', sub: null }; // broad fallback
      if (cl.includes('africa')) return { region: 'Africa', sub: null };
      if (cl.includes('oceania')) return { region: 'Oceania', sub: null };
    }
  }

  return { region: null, sub: null };
}

/**
 * Check if an hour falls within a window [start, end].
 * Handles overnight windows (e.g. [22, 2] means 22:00-02:00).
 */
function hourInWindow(hour, window) {
  if (!window || window.length !== 2) return true; // no window = always valid
  const [start, end] = window;
  if (start <= end) {
    return hour >= start && hour <= end;
  }
  // Overnight window (e.g., [22, 2])
  return hour >= start || hour <= end;
}

/**
 * Match a city pair against the route rules.
 * Returns the best-matching rule, or null.
 */
function matchRouteRule(fromRegion, toRegion) {
  if (!fromRegion.region || !toRegion.region) return null;

  // Helper: does region R match rule region string?
  const regionMatches = (ruleRegion, cityRegion) => {
    const r = ruleRegion.toLowerCase();
    const c = cityRegion.toLowerCase();
    // Exact match
    if (r === c) return true;
    // "Africa" matches "North Africa", "East Africa", "Southern Africa"
    if (r === 'africa' && c.includes('africa')) return true;
    if (r === 'south america' && c.includes('south america')) return true;
    if (r === 'north america' && c.includes('north america')) return true;
    // "East Asia" also matches for generic "Asia" rules
    if (r.includes('asia') && c.includes('asia')) return true;
    return false;
  };

  let bestRule = null;
  let bestScore = -1;

  for (const rule of ROUTE_RULES) {
    if (!regionMatches(rule.from, fromRegion.region)) continue;
    if (!regionMatches(rule.to, toRegion.region)) continue;

    let score = 1;
    // Sub-region bonus: if rule specifies subFrom/subTo and city matches, it's more specific
    if (rule.subFrom && fromRegion.sub === rule.subFrom) score += 10;
    if (rule.subTo && toRegion.sub === rule.subTo) score += 10;
    // Penalty if rule requires sub but city doesn't match
    if (rule.subFrom && fromRegion.sub && fromRegion.sub !== rule.subFrom) score -= 5;
    if (rule.subTo && toRegion.sub && toRegion.sub !== rule.subTo) score -= 5;

    if (score > bestScore) {
      bestScore = score;
      bestRule = rule;
    }
  }

  return bestRule;
}

/**
 * Estimate full flight schedule between two cities.
 * Uses route rules for realistic departure/arrival times + the existing
 * duration heuristics for flight time estimation.
 *
 * Times: departure in origin local time, arrival in destination local time.
 *
 * @param {string} fromCity
 * @param {string} toCity
 * @param {object} opts - { timezoneDiff?: number, helpers?: { getCityCountrySync, getContinentForCountry } }
 * @returns {{ departureTime, arrivalTime, duration, durationMinutes,
 *             arrivalDayOffset, isOvernight, routeType } | null}
 */
export function estimateFlightSchedule(fromCity, toCity, opts = {}) {
  const { timezoneDiff = 0, helpers = {} } = opts;

  // Get duration from existing heuristic
  const durationStr = estimateFlightDurationHeuristic(fromCity, toCity, helpers);
  if (!durationStr) return null;

  const durationMinutes = parseDurationToMinutes(durationStr);

  // Classify regions
  const fromRegion = classifyCityRegion(fromCity, helpers);
  const toRegion = classifyCityRegion(toCity, helpers);

  // Match route rule
  const rule = matchRouteRule(fromRegion, toRegion);

  let departureTime;
  let arrivalDayOffset;

  if (rule) {
    departureTime = rule.depart;
    arrivalDayOffset = rule.dayOffset;
  } else {
    // Fallback: short flights depart morning, long flights depart evening
    const hours = durationMinutes / 60;
    if (hours <= 4) {
      departureTime = '09:00';
      arrivalDayOffset = 0;
    } else if (hours <= 8) {
      departureTime = '10:00';
      arrivalDayOffset = 0;
    } else {
      departureTime = '20:00';
      arrivalDayOffset = 1;
    }
  }

  // Calculate arrival time: dep + duration + timezone diff
  const [depH, depM] = departureTime.split(':').map(Number);
  const depMin = depH * 60 + depM;
  const rawArrivalMin = depMin + durationMinutes + (timezoneDiff * 60);

  // Use computed day offset if we have a rule (rule knows the schedule pattern),
  // otherwise derive from the arithmetic
  let arrivalMin;
  if (rule) {
    // Rule-based: trust the day offset, compute arrival time from it
    arrivalMin = rawArrivalMin - (arrivalDayOffset * 1440);
    // Safety: if arithmetic doesn't agree with rule offset, clamp to valid range
    if (arrivalMin < 0) arrivalMin = ((arrivalMin % 1440) + 1440) % 1440;
    if (arrivalMin >= 1440) arrivalMin = arrivalMin % 1440;
  } else {
    // Arithmetic-based
    arrivalMin = rawArrivalMin;
    arrivalDayOffset = 0;
    if (arrivalMin >= 1440) {
      arrivalDayOffset = Math.floor(arrivalMin / 1440);
      arrivalMin = arrivalMin % 1440;
    } else if (arrivalMin < 0) {
      arrivalDayOffset = Math.floor(arrivalMin / 1440);
      arrivalMin = ((arrivalMin % 1440) + 1440) % 1440;
    }
  }

  // Avoid arrivals in 01:00-05:00 dead zone (curfew / no connections)
  if (arrivalMin >= 60 && arrivalMin <= 300 && !rule) {
    arrivalMin = 360; // push to 06:00
  }

  const arrH = Math.floor(arrivalMin / 60);
  const arrM = arrivalMin % 60;
  const arrivalTime = `${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}`;

  return {
    departureTime,
    arrivalTime,
    duration: durationStr,
    durationMinutes,
    arrivalDayOffset,
    isOvernight: arrivalDayOffset > 0,
    routeType: rule?.type || (durationMinutes > 480 ? 'long-haul' : 'short-haul'),
    _rule: rule || null, // expose matched rule for validation
  };
}

// ─── Connection Required Pairs ───────────────────────────────────────────────
// Region pairs with no nonstop commercial service (or very limited exceptions).
// Source of truth: packages/shared/data/connectionRequiredPairs.json
// Each entry: [originRegions[], destinationRegions[], typicalHubs[], connectionHoursMin, connectionHoursMax]
// Region strings match classifyCityRegion() output. "Africa" matches any African sub-region.

const CONNECTION_REQUIRED_PAIRS = [
  // Asia ↔ South America
  { origins: ['East Asia', 'Southeast Asia', 'South Asia', 'Asia'],
    destinations: ['South America'], hubs: ['LAX', 'SFO', 'DXB', 'DOH', 'AMS', 'MAD'], addHours: [4, 8] },
  // Africa ↔ Asia (West/Central/North Africa only — JNB/NBO/ADD have nonstops to select hubs)
  { origins: ['North Africa', 'Africa'],
    destinations: ['East Asia', 'Southeast Asia', 'South Asia', 'Asia'], hubs: ['DXB', 'DOH', 'AUH', 'IST'], addHours: [3, 6] },
  // Africa ↔ South America
  { origins: ['North Africa', 'East Africa', 'Africa'],
    destinations: ['South America'], hubs: ['LIS', 'MAD', 'CDG', 'AMS'], addHours: [4, 7] },
  // Africa ↔ Oceania
  { origins: ['North Africa', 'East Africa', 'Africa'],
    destinations: ['Oceania'], hubs: ['DXB', 'DOH', 'SIN', 'PER'], addHours: [4, 8] },
  // Europe ↔ Oceania (PER-LHR is sole exception)
  { origins: ['Europe'], destinations: ['Oceania'], hubs: ['DXB', 'DOH', 'SIN', 'HKG', 'BKK'], addHours: [3, 6] },
  // South America ↔ Oceania (SCL has nonstops to SYD/AKL)
  { origins: ['South America'], destinations: ['Oceania'], hubs: ['LAX', 'AKL', 'PPT'], addHours: [4, 8] },
  // South America ↔ Middle East (GRU/EZE have nonstops to DXB/DOH)
  { origins: ['South America'], destinations: ['Middle East'], hubs: ['MAD', 'LIS'], addHours: [3, 6] },
  // Central America/Caribbean ↔ Asia
  { origins: ['Central America'], destinations: ['East Asia', 'Southeast Asia', 'South Asia', 'Asia'],
    hubs: ['MEX', 'LAX', 'SFO', 'YYZ'], addHours: [4, 8] },
  // Central America/Caribbean ↔ Africa
  { origins: ['Central America'], destinations: ['Africa', 'North Africa', 'East Africa', 'Southern Africa'],
    hubs: ['MAD', 'LIS', 'JFK', 'IAD'], addHours: [4, 7] },
  // Central America/Caribbean ↔ Oceania
  { origins: ['Central America'], destinations: ['Oceania'], hubs: ['LAX', 'SFO', 'AKL'], addHours: [4, 8] },
  // Central America/Caribbean ↔ Middle East
  { origins: ['Central America'], destinations: ['Middle East'], hubs: ['JFK', 'IAD', 'LHR', 'CDG'], addHours: [3, 6] },
  // South Asia ↔ Oceania
  { origins: ['South Asia'], destinations: ['Oceania'], hubs: ['SIN', 'KUL', 'BKK', 'DXB'], addHours: [3, 6] },
];

/**
 * Check if a city pair requires a connection (no nonstop service).
 * Uses classifyCityRegion() to determine regions and matches against CONNECTION_REQUIRED_PAIRS.
 *
 * @param {string} fromCity
 * @param {string} toCity
 * @param {Object} helpers - { getCityCountrySync, getContinentForCountry }
 * @returns {{ required: boolean, typicalHubs?: string[], addHoursMin?: number, addHoursMax?: number } | null}
 */
export function requiresConnection(fromCity, toCity, helpers = {}) {
  if (!fromCity || !toCity) return null;

  const fromRegion = classifyCityRegion(fromCity, helpers);
  const toRegion = classifyCityRegion(toCity, helpers);

  if (!fromRegion.region || !toRegion.region) return null;

  // Helper: does a classified region match any entry in a list?
  const regionMatchesList = (regionStr, list) => {
    const r = regionStr.toLowerCase();
    return list.some(item => {
      const i = item.toLowerCase();
      if (r === i) return true;
      // "Africa" matches "North Africa", "East Africa", "Southern Africa"
      if (i === 'africa' && r.includes('africa')) return true;
      if (i === 'asia' && r.includes('asia')) return true;
      return false;
    });
  };

  // Check both directions (A→B and B→A)
  for (const pair of CONNECTION_REQUIRED_PAIRS) {
    const forwardMatch = regionMatchesList(fromRegion.region, pair.origins) &&
                         regionMatchesList(toRegion.region, pair.destinations);
    const reverseMatch = regionMatchesList(toRegion.region, pair.origins) &&
                         regionMatchesList(fromRegion.region, pair.destinations);

    if (forwardMatch || reverseMatch) {
      return {
        required: true,
        typicalHubs: pair.hubs,
        addHoursMin: pair.addHours[0],
        addHoursMax: pair.addHours[1],
      };
    }
  }

  return { required: false };
}

/**
 * Validate and correct LLM-provided flight transport data.
 * - Fills missing departure/arrival times from route-rule estimates.
 * - Corrects wildly wrong durations (>50% off from heuristic).
 * - Detects connection-required routes and adjusts duration upward.
 * - Preserves LLM carrier names (can't validate those).
 *
 * @param {object} llmEntry - { departureTime, arrivalTime, duration, carrier, ... }
 * @param {string} fromCity
 * @param {string} toCity
 * @param {object} opts - { timezoneDiff, helpers }
 * @returns {object} corrected entry with _estimated/_corrected flags
 */
export function validateFlightTransport(llmEntry, fromCity, toCity, opts = {}) {
  const estimate = estimateFlightSchedule(fromCity, toCity, opts);
  if (!estimate) return llmEntry; // can't validate, return as-is

  const result = { ...llmEntry };

  // Fill missing times from estimates
  if (!result.departureTime) {
    result.departureTime = estimate.departureTime;
    result._departureEstimated = true;
  }
  if (!result.arrivalTime) {
    result.arrivalTime = estimate.arrivalTime;
    result._arrivalEstimated = true;
  }

  // Validate LLM departure time against route scheduling window.
  // If the LLM says 09:00 AM for a transatlantic eastbound (window 17:00-23:00), override it.
  if (result.departureTime && !result._departureEstimated && estimate._rule?.window) {
    const [depH] = result.departureTime.split(':').map(Number);
    if (!hourInWindow(depH, estimate._rule.window)) {
      result._originalDepartureTime = result.departureTime;
      result.departureTime = estimate.departureTime;
      result.arrivalTime = estimate.arrivalTime; // recalculate arrival too
      result._departureCorrected = true;
    }
  }

  // Check LLM duration against heuristic estimate (flag if >50% off)
  if (result.duration) {
    const llmMin = parseDurationToMinutes(result.duration);
    const errorRatio = Math.abs(llmMin - estimate.durationMinutes) / estimate.durationMinutes;
    if (errorRatio > 0.5) {
      result.duration = estimate.duration;
      result._durationCorrected = true;
    }
  } else {
    result.duration = estimate.duration;
  }

  // Check if route requires a connection (no nonstop service)
  const connectionCheck = requiresConnection(fromCity, toCity, opts.helpers || {});
  if (connectionCheck?.required) {
    result.requiresConnection = true;
    result.typicalHubs = connectionCheck.typicalHubs;

    // Adjust duration upward to account for connection time (layover + connecting flight)
    const currentMin = parseDurationToMinutes(result.duration);
    const connectionMinutesMin = connectionCheck.addHoursMin * 60;
    // Only adjust if current duration doesn't already include connection time.
    // Heuristic: if the duration is less than nonstop + minimum connection overhead, add connection time.
    // Use midpoint of connection range (e.g., 3-6hr → add 4.5hr) for the adjustment.
    const connectionMidpointMin = Math.round(((connectionCheck.addHoursMin + connectionCheck.addHoursMax) / 2) * 60);
    if (currentMin < estimate.durationMinutes + connectionMinutesMin) {
      const adjustedMin = currentMin + connectionMidpointMin;
      const adjH = Math.floor(adjustedMin / 60);
      const adjM = adjustedMin % 60;
      result.duration = adjM > 0 ? `~${adjH}h ${adjM}m` : `~${adjH}h`;
      result._connectionAdjusted = true;

      // Recalculate arrival time with connection overhead
      if (result.departureTime) {
        const [depH, depM] = result.departureTime.split(':').map(Number);
        const depMinTotal = depH * 60 + depM;
        const tzDiff = opts.timezoneDiff || 0;
        let rawArr = depMinTotal + adjustedMin + (tzDiff * 60);
        let newDayOffset = result._arrivalDayOffset || 0;
        if (rawArr >= 1440) {
          newDayOffset = Math.floor(rawArr / 1440);
          rawArr = rawArr % 1440;
        } else if (rawArr < 0) {
          newDayOffset = Math.floor(rawArr / 1440);
          rawArr = ((rawArr % 1440) + 1440) % 1440;
        }
        const arrHr = Math.floor(rawArr / 60);
        const arrMn = rawArr % 60;
        result.arrivalTime = `${String(arrHr).padStart(2, '0')}:${String(arrMn).padStart(2, '0')}`;
        result._arrivalDayOffset = newDayOffset;
        result._isOvernight = newDayOffset > 0;
      }
    }
  }

  // Attach estimate metadata for downstream use
  result._estimatedDurationMin = estimate.durationMinutes;
  result._routeType = estimate.routeType;
  if (result._isOvernight == null) result._isOvernight = estimate.isOvernight;
  if (result._arrivalDayOffset == null) result._arrivalDayOffset = estimate.arrivalDayOffset;

  // Final invariant check: arrival ≈ departure + duration + tzDiff. The
  // logic above trusts the LLM's arrivalTime if departure happened to be
  // in-window for some matched rule, which let through hallucinated values
  // like "9:00 AM departure → Arrives 08:00". The reconciler catches the
  // residual cases the rule-based path misses.
  const reconciled = reconcileTransportTimes(
    {
      departureTime: result.departureTime,
      arrivalTime: result.arrivalTime,
      duration: result.duration,
      arrivalDayOffset: result._arrivalDayOffset,
    },
    { timezoneDiff: opts.timezoneDiff || 0 },
  );
  if (reconciled._timeReconciled) {
    result.arrivalTime = reconciled.arrivalTime;
    result._arrivalDayOffset = reconciled.arrivalDayOffset;
    result._isOvernight = reconciled.arrivalDayOffset > 0;
    result._timeReconciled = true;
    result._reconcileReason = reconciled._reconcileReason;
  }

  return result;
}
